import { Contrast, Eye } from 'lucide-react';
import { useHighContrast } from '../hooks/useHighContrast';

export function HighContrastToggle() {
  const { isHighContrast, setHighContrast } = useHighContrast();

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          High-Contrast Mode
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Enable enhanced contrast for improved accessibility (WCAG AAA compliant)
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => setHighContrast(false)}
          className={`
            relative p-4 rounded-lg border-2 transition-all duration-200
            ${
              !isHighContrast
                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
            }
          `}
          aria-pressed={!isHighContrast}
          aria-label="Standard contrast mode"
        >
          <div className="flex flex-col items-center gap-2">
            <div
              className={`
                p-3 rounded-full transition-colors
                ${
                  !isHighContrast
                    ? 'bg-blue-100 dark:bg-blue-800/30 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }
              `}
            >
              <Eye className="w-6 h-6" />
            </div>
            <div className="text-center">
              <div
                className={`
                  font-semibold
                  ${
                    !isHighContrast
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-900 dark:text-gray-100'
                  }
                `}
              >
                Standard
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Normal contrast levels
              </div>
            </div>
          </div>
          {!isHighContrast && (
            <div className="absolute top-2 right-2">
              <div className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400" />
            </div>
          )}
        </button>

        <button
          onClick={() => setHighContrast(true)}
          className={`
            relative p-4 rounded-lg border-2 transition-all duration-200
            ${
              isHighContrast
                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
            }
          `}
          aria-pressed={isHighContrast}
          aria-label="High contrast mode"
        >
          <div className="flex flex-col items-center gap-2">
            <div
              className={`
                p-3 rounded-full transition-colors
                ${
                  isHighContrast
                    ? 'bg-blue-100 dark:bg-blue-800/30 text-blue-600 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }
              `}
            >
              <Contrast className="w-6 h-6" />
            </div>
            <div className="text-center">
              <div
                className={`
                  font-semibold
                  ${
                    isHighContrast
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-900 dark:text-gray-100'
                  }
                `}
              >
                High Contrast
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Enhanced accessibility
              </div>
            </div>
          </div>
          {isHighContrast && (
            <div className="absolute top-2 right-2">
              <div className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400" />
            </div>
          )}
        </button>
      </div>

      <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
          About High-Contrast Mode
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• Meets WCAG AAA standards with 7:1 minimum contrast ratio</li>
          <li>• Increases border widths for better element distinction</li>
          <li>• Uses stronger color differentiation for improved readability</li>
          <li>• Works with both light and dark themes</li>
          <li>• Optimized for screen readers and keyboard navigation</li>
          <li>• Your preference is saved and persists across sessions</li>
        </ul>
      </div>

      {isHighContrast && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-3">
            <Contrast className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                High-Contrast Mode Active
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                All interactive elements now have enhanced contrast ratios for improved accessibility.
                This mode is especially helpful for users with low vision or color perception difficulties.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
